import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import { complaintAPI, visitorAPI, activityAPI, userAPI, unitAPI, organizationAPI } from '../../services/api';
import { noticeAPI } from '../../services/api';
import { formatDwellingUnit, getSocietyName } from '../../utils/brandingUtils';
import PageTitle from '../shared/PageTitle';
import '../../styles/Dashboard.css';
import '../../styles/IconFix.css';
import '../../styles/ThemeOverrides.css';

const Dashboard = () => {
  const { currentUser } = useContext(AuthContext);
  const { settings } = useSettings();
  const [stats, setStats] = useState({
    totalUnits: 0,
    occupiedUnits: 0,
    pendingComplaints: 0,
    todayVisitors: 0,
    totalUsers: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgSettings, setOrgSettings] = useState(null);
  const [lastThemeUpdate, setLastThemeUpdate] = useState(Date.now());
  const [rules, setRules] = useState([]);
  const [announcements, setAnnouncements] = useState([]);

  // Get dwelling unit name (plural)
  // const unitSingular = formatDwellingUnit(settings);
  const unitPlural = formatDwellingUnit(settings, null, true);

  // Apply theme directly from database
  const applyThemeDirectly = useCallback(() => {
    try {
      if (!orgSettings) {
        return;
      }

      // Get theme values directly from orgSettings
      const themeColors = {
        primaryColor: orgSettings.theme_primary_color || '#0d6efd',
        secondaryColor: orgSettings.theme_secondary_color || '#6c757d',
        sidebarBgColor: orgSettings.sidebar_bg_color || '#ffffff',
        sidebarTextColor: orgSettings.sidebar_text_color || '#212529',
        sidebarActiveBgColor: orgSettings.sidebar_active_bg_color || '#0d6efd',
        sidebarActiveTextColor: orgSettings.sidebar_active_text_color || '#ffffff',
        blockTitleColor: orgSettings.block_title_color || '#8B4513',
        blockTitleBgColor: orgSettings.block_title_bg_color || '#f8f9fa',
      };

      // Set user role attribute on body element
      document.body.setAttribute('data-user-role', currentUser?.role || 'anonymous');

      // Create or update admin dashboard theme styles
      let adminThemeStyle = document.getElementById('admin-dashboard-theme');
      if (!adminThemeStyle) {
        adminThemeStyle = document.createElement('style');
        adminThemeStyle.id = 'admin-dashboard-theme';
        document.head.appendChild(adminThemeStyle);
      }

      // Apply theme through style tag
      adminThemeStyle.innerHTML = `
        /* Admin Dashboard Theme Colors */
        .btn-primary {
          background-color: ${themeColors.primaryColor} !important;
          border-color: ${themeColors.primaryColor} !important;
        }
        
        .btn-primary:hover,
        .btn-primary:focus,
        .btn-primary:active {
          background-color: ${themeColors.primaryColor}dd !important;
          border-color: ${themeColors.primaryColor}dd !important;
        }
        
        .text-primary {
          color: ${themeColors.primaryColor} !important;
        }
        
        .dashboard-stat-card {
          border-left: 4px solid ${themeColors.primaryColor} !important;
        }
        
        .dashboard-stat-card .stat-value {
          color: ${themeColors.primaryColor} !important;
        }
        
        .page-title {
          color: ${themeColors.primaryColor} !important;
        }
        
        .card-header {
          background-color: ${themeColors.blockTitleBgColor} !important;
          color: ${themeColors.blockTitleColor} !important;
        }
        
        .card-header h1, .card-header h2, .card-header h3,
        .card-header h4, .card-header h5, .card-header h6 {
          color: ${themeColors.blockTitleColor} !important;
        }
        
        .sidebar-navigation {
          background-color: ${themeColors.sidebarBgColor} !important;
        }
        
        .sidebar-navigation .menu-item.active,
        .sidebar-navigation li.active,
        .sidebar-navigation .active {
          background-color: ${themeColors.sidebarActiveBgColor} !important;
        }
        
        .sidebar-navigation .menu-item.active *,
        .sidebar-navigation li.active *,
        .sidebar-navigation .active * {
          color: ${themeColors.sidebarActiveTextColor} !important;
        }
      `;

      // Update last theme update timestamp
      setLastThemeUpdate(Date.now());

      // Trigger a global theme update event
      const event = new CustomEvent('apply-organization-theme');
      window.dispatchEvent(event);
    } catch (error) {
      console.error('Error in applyThemeDirectly:', error);
    }
  }, [orgSettings, currentUser]);

  // Debug function to get computed CSS variable values
  const getComputedCssVars = () => {
    const cssVars = {};
    const root = document.documentElement;
    const computed = getComputedStyle(root);

    // Get important theme variables
    const vars = [
      '--primary-color',
      '--secondary-color',
      '--bs-primary',
      '--sidebar-bg-color',
      '--sidebar-text-color',
      '--sidebar-active-bg-color',
      '--sidebar-active-text-color',
      '--block-title-color',
      '--block-title-bg-color'
    ];

    vars.forEach(varName => {
      cssVars[varName] = computed.getPropertyValue(varName);
    });

    return cssVars;
  };

  // Collect debug data
  useEffect(() => {
    const collectDebugData = async () => {
      try {
        // Create debug object
        const debug = {
          settings: { ...settings },
          user: { ...currentUser },
          computedCss: getComputedCssVars(),
          document_theme: document.documentElement.getAttribute('data-bs-theme'),
          currentOrgSettings: {},
          lastThemeUpdate: new Date(lastThemeUpdate).toLocaleTimeString(),
          bodyUserRole: document.body.getAttribute('data-user-role') || 'not set'
        };

        // Current theme settings from org settings
        if (orgSettings) {
          debug.currentOrgSettings = {
            theme_primary_color: orgSettings.theme_primary_color || 'not set',
            theme_secondary_color: orgSettings.theme_secondary_color || 'not set',
            sidebar_bg_color: orgSettings.sidebar_bg_color || 'not set',
            sidebar_text_color: orgSettings.sidebar_text_color || 'not set',
            sidebar_active_bg_color: orgSettings.sidebar_active_bg_color || 'not set',
            sidebar_active_text_color: orgSettings.sidebar_active_text_color || 'not set',
            block_title_color: orgSettings.block_title_color || 'not set',
            block_title_bg_color: orgSettings.block_title_bg_color || 'not set',
            society_name: orgSettings.society_name || 'not set'
          };
        }

        // Try to get organization data - only do this if we don't already have it
        try {
          if (currentUser?.organization_id && !orgSettings) {
            const orgResponse = await organizationAPI.getOrganizationSettings(currentUser.organization_id);
            debug.organization = orgResponse.data;
            setOrgSettings(orgResponse.data);
          }
        } catch (error) {
          debug.orgError = error.message;
        }

      } catch (e) {
        console.error('Error collecting debug data:', e);
      }
    };

    collectDebugData();
  }, [currentUser, settings, orgSettings, lastThemeUpdate]);

  // Load organization settings and ensure we have the latest settings
  useEffect(() => {
    const loadOrgSettings = async () => {
      try {
        if (currentUser && currentUser.organization_id) {
          const orgResponse = await organizationAPI.getOrganizationSettings(currentUser.organization_id);
          setOrgSettings(orgResponse.data);
        }
      } catch (error) {
        console.error('Error loading organization settings:', error);
      }
    };

    loadOrgSettings();

    // Set up periodic refresh of organization settings
    const refreshInterval = setInterval(() => {
      loadOrgSettings();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(refreshInterval);
  }, [currentUser]);

  // Apply theme when orgSettings change
  useEffect(() => {
    if (orgSettings) {
      applyThemeDirectly();
    }
  }, [orgSettings, applyThemeDirectly]);

  // Set user role on body when component loads
  useEffect(() => {
    if (currentUser) {
      document.body.setAttribute('data-user-role', currentUser.role || 'anonymous');
    }
  }, [currentUser]);

  // Function to get pending complaints
  const getPendingComplaints = async () => {
    try {
      const complaintsRes = await complaintAPI.getAllComplaints({ status: 'open' });
      return { data: { pending: complaintsRes.data.length } };
    } catch (err) {
      console.error('Error with standard endpoint, trying owner endpoint:', err);
      try {
        console.log('Owner endpoint response:');
      } catch (fallbackErr) {
        console.error('Owner endpoint also failed:', fallbackErr);
        return { data: { pending: 0 } }; // default fallback
      }
    }
  };

  // Function to fetch dashboard stats (move this above refreshStats and useEffect)
  const fetchStats = useCallback(async () => {
    try {
      // Use Promise.allSettled to handle multiple API calls,
      // so if one fails, the others can still complete
      const [unitStats, complaintStats, visitorStats, userStats, activityResults] =
        await Promise.allSettled([
          unitAPI.getUnitStats().catch(() => {
            return { data: { total: 0, occupied: 0 } };
          }),
          getPendingComplaints(),
          visitorAPI.getComprehensiveStats().catch(() => {
            return { data: { activeCount: 0, todayCount: 0 } };
          }),
          userAPI.getUserStats().catch(() => {
            return { data: { total: 0 } };
          }),
          activityAPI.getRecentActivities(5).catch(() => {
            return { data: { activities: [] } };
          })
        ]);

      setStats(prevStats => {
        const newStats = { ...prevStats };
        if (unitStats.status === 'fulfilled' && unitStats.value && unitStats.value.data) {
          newStats.totalUnits = unitStats.value.data.total || prevStats.totalUnits;
          newStats.occupiedUnits = unitStats.value.data.occupied || prevStats.occupiedUnits;
        }
        if (complaintStats.status === 'fulfilled' && complaintStats.value && complaintStats.value.data) {
          newStats.pendingComplaints = complaintStats.value.data.pending || prevStats.pendingComplaints;
        }
        if (visitorStats.status === 'fulfilled' && visitorStats.value && visitorStats.value.data) {
          newStats.todayVisitors = visitorStats.value.data.activeCount || prevStats.todayVisitors;
        }
        if (userStats.status === 'fulfilled' && userStats.value && userStats.value.data) {
          newStats.totalUsers = userStats.value.data.total || prevStats.totalUsers;
        }
        return newStats;
      });


      // Update activity logs if available
      if (activityResults.status === 'fulfilled' && activityResults.value && activityResults.value.data) {
        const activities = activityResults.value.data.activities || [];
        setRecentActivities(activities);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      setLoading(false);
    }
  }, []);

  // Function to manually refresh stats
  const refreshStats = useCallback(() => {
    // Create a refresh effect by setting loading to true briefly
    setLoading(true);

    // Call fetchStats function to get latest data
    fetchStats();
  }, [fetchStats]);

  // Function to get icon for activity type
  const getActivityIcon = (type) => {
    switch (type) {
      case 'visitor':
        return 'fas fa-user-check text-info';
      case 'complaint':
        return 'fas fa-exclamation-circle text-warning';
      case 'payment':
        return 'fas fa-money-bill-wave text-success';
      case 'user':
        return 'fas fa-user-plus text-primary';
      default:
        return 'fas fa-bell text-secondary';
    }
  };

  // Load data when component mounts
  useEffect(() => {
    // Check API endpoints before fetching data
    const checkEndpoints = async () => {
      try {
        fetchStats();
      } catch (error) {
        console.error('Error checking API endpoints:', error);
        fetchStats();
      }
    };

    checkEndpoints();
  }, [fetchStats]);

  // Fetch notices (rules and announcements) periodically
  useEffect(() => {
    const fetchNotices = async () => {
      try {
        const [rulesRes, annRes] = await Promise.allSettled([
          noticeAPI.getRules(),
          noticeAPI.getAnnouncements(),
        ]);

        // Handle rules response
        if (rulesRes.status === 'fulfilled' && rulesRes.value && rulesRes.value.data) {
          // Ensure data is an array
          const rulesData = Array.isArray(rulesRes.value.data) ? rulesRes.value.data : [];
          setRules(rulesData);
        } else {
          console.error('Rules fetch failed:', rulesRes.reason || 'Unknown error');
          setRules([]); // Fallback to empty array
        }

        // Handle announcements response
        if (annRes.status === 'fulfilled' && annRes.value && annRes.value.data) {
          // Ensure data is an array
          const announcementsData = Array.isArray(annRes.value.data) ? annRes.value.data : [];
          setAnnouncements(announcementsData);
        } else {
          console.error('Announcements fetch failed:', annRes.reason || 'Unknown error');
          setAnnouncements([]); // Fallback to empty array
        }
      } catch (err) {
        console.error('Error fetching notices:', err);
        // Ensure arrays are always set even on complete failure
        setRules([]);
        setAnnouncements([]);
      }
    };
    fetchNotices();
    const interval = setInterval(fetchNotices, 30000);
    return () => clearInterval(interval);
  }, []);

  // Show a loading spinner if data is still being fetched
  if (loading) {
    return (
      <div className="container-fluid mt-4 activity-container">
        <PageTitle
          icon="fa-tachometer-alt"
          title="Admin Dashboard"
        />

        <div className="alert alert-info mb-4">
          <div className="d-flex align-items-center">
            <i className="fas fa-info-circle fa-2x me-3"></i>
            <div>
              <h5 className="mb-1">Welcome to {getSocietyName(settings)} Management</h5>
              <p className="mb-0">Loading dashboard data...</p>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4 activity-container">
      {/* Debug Panel removed */}

      {/* Header with refresh button */}
      <PageTitle
        icon="fa-tachometer-alt"
        title="Admin Dashboard"
        action={
          <button
            className="btn btn-outline-primary"
            onClick={refreshStats}
            disabled={loading}
          >
            <i className={`fas fa-sync ${loading ? 'fa-spin' : ''}`}></i> {loading ? 'Refreshing...' : 'Refresh Data'}
          </button>
        }
      />

      {/* Welcome message */}
      <div className="alert alert-info mb-4">
        <div className="d-flex align-items-center">
          <i className="fas fa-info-circle fa-2x me-3"></i>
          <div>
            <h5 className="mb-1">Welcome to {getSocietyName(settings)} Management</h5>
            <p className="mb-0">Manage your society with ease. Quick access to all functions is available from this dashboard.</p>
          </div>
        </div>
      </div>

      {/* Dashboard stats - updated to match visitors page layout */}
      <div className="dashboard-stats-container">
        <div className="dashboard-stat-card units">
          <div className="stat-title">TOTAL UNITS</div>
          <div className="stat-value" title={stats.totalUnits}>
            {stats.totalUnits}
          </div>
          <div className="stat-description">
            {stats.totalUnits > 0 ?
              `${Math.round((stats.occupiedUnits / stats.totalUnits) * 100)}% Occupied` :
              'No units yet'}
          </div>
        </div>

        <div className="dashboard-stat-card complaints">
          <div className="stat-title">PENDING COMPLAINTS / SUGGESTIONS</div>
          <div className="stat-value" title={stats.pendingComplaints}>
            {stats.pendingComplaints}
          </div>
          <div className="stat-description">
            Needs attention
          </div>
        </div>

        <div className="dashboard-stat-card visitors">
          <div className="stat-title">TODAY&apos;S VISITORS</div>
          <div className="stat-value" title={stats.todayVisitors}>
            {stats.todayVisitors}
          </div>
          <div className="stat-description">
            Active now
          </div>
        </div>

        <div className="dashboard-stat-card users">
          <div className="stat-title">TOTAL USERS</div>
          <div className="stat-value" title={stats.totalUsers}>
            {stats.totalUsers}
          </div>
          <div className="stat-description">
            All roles
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card dashboard-card">
            <div className="card-header bg-danger text-white">
              <h5 className="mb-0">Quick Actions</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-lg-3 col-md-6 mb-3">
                  <Link to="/admin/financials" className="btn btn-primary w-100 d-flex align-items-center justify-content-between">
                    <span>Record Payment</span>
                    <i className="fas fa-money-bill-wave"></i>
                  </Link>
                </div>
                <div className="col-lg-3 col-md-6 mb-3">
                  <Link to="/admin/users" className="btn btn-primary w-100 d-flex align-items-center justify-content-between">
                    <span>Add User</span>
                    <i className="fas fa-user-plus"></i>
                  </Link>
                </div>
                <div className="col-lg-3 col-md-6 mb-3">
                  <Link to="/admin/units" className="btn btn-primary w-100 d-flex align-items-center justify-content-between">
                    <span>Manage Units</span>
                    {/* <span>Manage {unitPlural}</span> */}
                    <i className="fas fa-building"></i>
                  </Link>
                </div>
                <div className="col-lg-3 col-md-6 mb-3">
                  <Link to="/admin/visitors" className="btn btn-primary w-100 d-flex align-items-center justify-content-between">
                    <span>View Visitors</span>
                    <i className="fas fa-eye"></i>
                  </Link>
                </div>
              </div>
            </div>
            <div className="card-footer text-end">
              <Link to="/admin/activity" className="btn btn-outline-primary">View All Activity <i className="fas fa-arrow-right ms-1"></i></Link>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity and At a Glance */}
      <div className="row">
        <div className="col-lg-6 col-md-12 mb-3">
          <div className="card h-100">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">Recent Activities</h5>
            </div>
            <div className="card-body p-0">
              <div className="activity-list">
                {recentActivities.length === 0 ? (
                  <div className="text-center p-4">
                    <i className="fas fa-bell fa-2x text-muted mb-3"></i>
                    <p>No recent activities</p>
                  </div>
                ) : (
                  recentActivities.map(activity => (
                    <div key={activity.id} className="activity-item d-flex align-items-center p-3 border-bottom">
                      <div className="activity-icon me-3 rounded-circle p-2 bg-light">
                        <i className={getActivityIcon(activity.type)}></i>
                      </div>
                      <div className="activity-details flex-grow-1">
                        <div className="activity-message">{activity.message}</div>
                        <div className="d-flex justify-content-between mt-1">
                          <div className="activity-user small">
                            <i className="fas fa-user me-1"></i> {activity.user || 'System'}
                          </div>
                          <div className="activity-time text-muted small">
                            {activity.time || 'Recently'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="card-footer text-end">
              <Link to="/admin/activity" className="btn btn-outline-primary">View All Activity <i className="fas fa-arrow-right ms-1"></i></Link>
            </div>
          </div>
        </div>

        <div className="col-lg-6 col-md-12 mb-4">
          <div className="card dashboard-card h-100">
            <div className="card-header bg-success text-white">
              <h5 className="mb-0">At a Glance</h5>
            </div>
            <div className="card-body">
              <div className="at-glance-item d-flex justify-content-between align-items-center mb-3">
                <span className="label">Vacant {unitPlural}</span>
                <span className="value badge bg-primary rounded-pill">{stats.totalUnits - stats.occupiedUnits}</span>
              </div>
              <div className="at-glance-item d-flex justify-content-between align-items-center mb-3">
                <span className="label">Pending Complaints</span>
                <span className="value badge bg-warning rounded-pill">{stats.pendingComplaints}</span>
              </div>
              <div className="at-glance-item d-flex justify-content-between align-items-center mb-3">
                <span className="label">Active Visitors</span>
                {console.log(stats, "statsstatsstats")}
                <span className="value badge bg-info rounded-pill">{stats.todayVisitors}</span>
              </div>
              <div className="at-glance-item d-flex justify-content-between align-items-center">
                <span className="label">Total Users</span>
                <span className="value badge bg-success rounded-pill">{stats.totalUsers}</span>
              </div>
            </div>
            <div className="card-footer text-end">
              <Link to="/admin/reports" className="btn btn-outline-primary">View Reports <i className="fas fa-arrow-right ms-1"></i></Link>
            </div>
          </div>
        </div>
      </div>

      {/* Notices Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header bg-info text-white">
              <h5 className="mb-0">Notices</h5>
            </div>
            <div className="card-body">
              <h6><strong>Rules</strong></h6>
              {rules.length === 0 ? (
                <p>No rules available.</p>
              ) : (
                <ul className="list-group mb-3">
                  {rules.map(rule => (
                    <li key={rule.id} className="list-group-item">
                      <strong>{rule.title}</strong>
                      <p className="mb-1">{rule.description}</p>
                    </li>
                  ))}
                </ul>
              )}
              <h6><strong>Announcements</strong></h6>
              {announcements.length === 0 ? (
                <p>No announcements available.</p>
              ) : (
                <ul className="list-group">
                  {announcements.map(ann => (
                    <li key={ann.id} className="list-group-item">
                      <strong>{ann.title}</strong>
                      <small className="text-muted ms-2">
                        {new Date(ann.start_date).toLocaleDateString()}
                        {ann.expiry_date ? ` - ${new Date(ann.expiry_date).toLocaleDateString()}` : ''}
                      </small>
                      <p className="mb-1">{ann.description}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Dashboard; 